/**
 * Public Holiday Surcharge — client-side mirror of
 * `backend/services/public_holiday_service.py`.
 *
 * iter_PUBLIC_HOLIDAY_SURCHARGE (2026-02-28) — Owner/manager manually
 * configures surcharge dates per venue. NO built-in Victorian calendar.
 *
 * The cart preview uses this helper so the staff/customer see the
 * exact amount the backend will stamp on the order. The backend is
 * still the source of truth for the snapshot at create-time.
 */
const DEFAULT_TZ = "Australia/Melbourne";

function _resolveTz(venue) {
  const tz =
    venue?.settings?.timezone || venue?.timezone || venue?.settings?.tz;
  return tz || DEFAULT_TZ;
}

function _todayLocalDate(venue, now = new Date()) {
  const tz = _resolveTz(venue);
  try {
    // en-CA locale yields YYYY-MM-DD which matches ISO date.
    const fmt = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    return fmt.format(now);
  } catch (_e) {
    // Fallback: UTC ISO date if the runtime doesn't know the timezone.
    return now.toISOString().slice(0, 10);
  }
}

export function getPublicHolidayConfig(venue) {
  const raw = venue?.settings?.public_holiday_surcharge || {};
  const enabled = !!raw.enabled;
  let value = Number(raw.value) || 0;
  if (value < 0) value = 0;
  if (value > 100) value = 100;
  const at = Array.isArray(raw.applies_to_order_types)
    ? raw.applies_to_order_types.map((x) => String(x || "").toLowerCase())
    : [];
  const applies_to_order_types = Array.from(new Set(at.filter(Boolean))).sort();
  const rawDates = Array.isArray(raw.dates) ? raw.dates : [];
  const seen = new Set();
  const dates = [];
  for (const item of rawDates) {
    let d = null;
    let label = "";
    if (item && typeof item === "object") {
      d = item.date;
      label = (item.label || "").toString().trim();
    } else if (typeof item === "string") {
      d = item;
    }
    if (typeof d !== "string") continue;
    d = d.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) continue;
    if (seen.has(d)) continue;
    seen.add(d);
    dates.push({ date: d, label: label.slice(0, 80) });
  }
  dates.sort((a, b) => a.date.localeCompare(b.date));
  return {
    enabled,
    type: "percent",
    value,
    applies_to_order_types,
    dates,
  };
}

export function resolvePublicHoliday(
  venue,
  orderType,
  { manualAction = null, now = new Date() } = {},
) {
  const cfg = getPublicHolidayConfig(venue);
  const dateLocal = _todayLocalDate(venue, now);
  const matched =
    cfg.enabled && cfg.dates.find((r) => r.date === dateLocal) || null;
  const orderTypeNorm = String(orderType || "").toLowerCase();
  const allowed = new Set(cfg.applies_to_order_types || []);
  const autoOk =
    !!matched &&
    cfg.enabled &&
    (allowed.size === 0 || allowed.has(orderTypeNorm));

  const action = ["auto", "add", "remove"].includes(
    (manualAction || "auto").toString().toLowerCase(),
  )
    ? (manualAction || "auto").toString().toLowerCase()
    : "auto";

  let applies = false;
  let override = null;
  if (action === "remove") {
    applies = false;
    override = "remove";
  } else if (action === "add") {
    applies = cfg.enabled && cfg.value > 0;
    override = "add";
  } else {
    applies = autoOk && cfg.value > 0;
  }
  return {
    applies,
    rate: cfg.value,
    dateLocal,
    label: matched?.label || "",
    autoMatched: !!matched && autoOk,
    override,
    orderType: orderTypeNorm,
    configuredToday: !!matched,
  };
}

export function computePublicHolidayAmount(baseDollars, ratePercent) {
  const base = Number(baseDollars) || 0;
  let rate = Number(ratePercent) || 0;
  if (rate < 0) rate = 0;
  if (rate > 100) rate = 100;
  if (base <= 0 || rate <= 0) return 0;
  return Math.round((base * rate) / 100 * 100) / 100;
}
